<template>
  <div class="report-page">
    <!-- Header -->
    <div class="report-header">
      <div>
        <h3 class="mb-0">Solstice System Report</h3>
        <small class="text-muted">{{ report ? report.range.label : periodLabel }}</small>
      </div>
      <div class="report-header-actions">
        <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="load">
          <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />Refresh
        </b-button>
        <b-button variant="outline-secondary" size="sm" :disabled="!report" @click="print">
          <feather-icon icon="PrinterIcon" size="14" class="mr-25" />Print
        </b-button>
        <b-button variant="primary" size="sm" :disabled="downloading || !report" @click="downloadPdf">
          <b-spinner v-if="downloading" small class="mr-25" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-25" />Download PDF
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <b-card class="filter-card" no-body>
      <div class="filter-grid">
        <div class="filter-field">
          <label for="report-period">Period</label>
          <b-form-select id="report-period" v-model="filters.period" :options="periodOptions" size="sm" />
        </div>

        <div v-if="filters.period === 'custom'" class="filter-field filter-field--range">
          <label for="report-range">From – to</label>
          <flat-pickr
            id="report-range"
            v-model="customRange"
            :config="rangeConfig"
            class="form-control form-control-sm"
            placeholder="Pick a start and end date"
          />
        </div>

        <div class="filter-field">
          <label>Project type</label>
          <b-button-group size="sm" class="type-toggle">
            <b-button
              v-for="option in typeOptions"
              :key="option.value || 'all'"
              :variant="filters.type === option.value ? 'primary' : 'outline-secondary'"
              :aria-pressed="filters.type === option.value ? 'true' : 'false'"
              @click="filters.type = option.value"
            >{{ option.text }}</b-button>
          </b-button-group>
        </div>

        <div class="filter-field filter-field--department">
          <label for="report-department">Department</label>
          <v-select
            id="report-department"
            v-model="filters.department"
            :options="departments"
            placeholder="All departments"
            :clearable="true"
            class="department-select"
          />
        </div>

        <div class="filter-field filter-field--switch">
          <!-- A plain checkbox: the theme draws switches as a solid block without a knob. -->
          <b-form-checkbox v-model="filters.includeArchived">Include archived projects</b-form-checkbox>
        </div>

        <div class="filter-field filter-field--reset">
          <b-button variant="flat-secondary" size="sm" :disabled="isDefaultFilters" @click="resetFilters">Reset filters</b-button>
        </div>
      </div>

      <div class="filter-summary">
        <span>Projects created <b>{{ report ? report.range.label : periodLabel }}</b></span>
        <b-badge v-if="filters.type" pill variant="light-primary" class="filter-chip">
          {{ filters.type === 'internal' ? 'Internal' : 'External' }}
          <button type="button" aria-label="Remove project type filter" @click="filters.type = null">×</button>
        </b-badge>
        <b-badge v-if="filters.department" pill variant="light-primary" class="filter-chip">
          {{ filters.department }}
          <button type="button" aria-label="Remove department filter" @click="filters.department = null">×</button>
        </b-badge>
        <b-badge v-if="!filters.includeArchived" pill variant="light-secondary" class="filter-chip">
          Archived excluded
          <button type="button" aria-label="Include archived projects" @click="filters.includeArchived = true">×</button>
        </b-badge>
      </div>
    </b-card>

    <b-alert :show="!!error" variant="danger" class="report-error">
      <div class="alert-body d-flex align-items-center justify-content-between flex-wrap">
        <span><feather-icon icon="AlertTriangleIcon" class="mr-50" />{{ error }}</span>
        <b-button size="sm" variant="danger" @click="load">Try again</b-button>
      </div>
    </b-alert>

    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <template v-if="report">
        <!-- Key figures -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <span class="kpi-label">Projects created</span>
            <span class="kpi-value">{{ report.summary.projects }}</span>
            <span class="kpi-detail">{{ report.summary.internal }} internal · {{ report.summary.external }} external</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-label">Quotes issued</span>
            <span class="kpi-value">{{ report.summary.quotes }}</span>
            <span class="kpi-detail">{{ money(report.summary.quotedValue) }} quoted</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-label">Quotes approved</span>
            <span class="kpi-value">{{ report.summary.approvedQuotes }}</span>
            <span class="kpi-detail">
              {{ money(report.summary.approvedValue) }}<template v-if="approvalRate !== null"> · {{ approvalRate }}% of quotes</template>
            </span>
          </div>
          <div class="kpi-card">
            <span class="kpi-label">Request to quote</span>
            <span class="kpi-value">{{ quoteTurnaround === null ? '—' : quoteTurnaround }}<small v-if="quoteTurnaround !== null"> days</small></span>
            <span class="kpi-detail">average turnaround</span>
          </div>
        </div>

        <!-- Projects over time + status -->
        <div class="chart-grid chart-grid--wide-left">
          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Projects created</h5>
              <small class="text-muted">by {{ report.createdOverTime.unit }}, internal and external</small>
            </div>
            <e-charts v-if="report.summary.projects" :options="createdChart" autoresize class="chart chart--tall" />
            <div v-else class="empty-state">No projects were created in this period.</div>
          </b-card>

          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Where those projects are now</h5>
              <small class="text-muted">current status</small>
            </div>
            <ul v-if="report.summary.projects" class="bar-list">
              <li v-for="status in report.statuses" :key="status.key" :class="{ muted: !status.count }">
                <div class="bar-list-row">
                  <span>{{ status.label }}</span>
                  <b>{{ status.count }}</b>
                </div>
                <div class="bar-track"><div class="bar-fill" :style="{ width: share(status.count, report.summary.projects) }"></div></div>
              </li>
            </ul>
            <div v-else class="empty-state">No projects to show.</div>
          </b-card>
        </div>

        <!-- Costs + stage times -->
        <div class="chart-grid">
          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Quote value breakdown</h5>
              <small class="text-muted">quotes dated in this period · {{ money(report.summary.quotedValue) }}</small>
            </div>
            <div v-if="report.summary.quotedValue > 0" class="breakdown">
              <e-charts :options="costChart" autoresize class="chart chart--donut" />
              <table class="breakdown-table">
                <tbody>
                  <tr v-for="(cost, index) in report.costBreakdown" :key="cost.key">
                    <td><span class="swatch" :style="{ background: palette[index] }"></span>{{ cost.name }}</td>
                    <td class="text-right">{{ money(cost.value) }}</td>
                    <td class="text-right text-muted">{{ share(cost.value, report.summary.quotedValue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">No quotes were dated in this period.</div>
          </b-card>

          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Average time per stage</h5>
              <small class="text-muted">days, for projects created in this period that reached both ends</small>
            </div>
            <ul class="bar-list">
              <li v-for="stage in report.stages" :key="stage.key" :class="{ muted: stage.averageDays === null, total: stage.key === 'total' }">
                <div class="bar-list-row">
                  <span>{{ stage.label }}</span>
                  <span>
                    <b>{{ stage.averageDays === null ? '—' : `${stage.averageDays} d` }}</b>
                    <span class="stage-count">{{ stage.projects }} {{ stage.projects === 1 ? 'project' : 'projects' }}</span>
                  </span>
                </div>
                <div class="bar-track"><div class="bar-fill bar-fill--stage" :style="{ width: stageShare(stage) }"></div></div>
              </li>
            </ul>
          </b-card>
        </div>

        <!-- Materials + rejections -->
        <div class="chart-grid">
          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Material popularity</h5>
              <small class="text-muted">parts ordered per material</small>
            </div>
            <e-charts v-if="report.materials.length" :options="materialChart" autoresize class="chart" :style="{ height: barHeight(report.materials) }" />
            <div v-else class="empty-state">No materials recorded on these projects.</div>
          </b-card>

          <b-card class="chart-card">
            <div class="chart-card-header">
              <h5>Reasons quotes were rejected</h5>
              <small class="text-muted">{{ report.summary.rejectedQuotes }} rejected {{ report.summary.rejectedQuotes === 1 ? 'quote' : 'quotes' }} in this period</small>
            </div>
            <e-charts v-if="report.rejectReasons.length" :options="rejectChart" autoresize class="chart" :style="{ height: barHeight(report.rejectReasons) }" />
            <div v-else class="empty-state">No rejection reasons were recorded in this period.</div>
          </b-card>
        </div>
      </template>

      <div v-else-if="loading" class="loading-placeholder"></div>
    </b-overlay>
  </div>
</template>

<script>
import { BCard, BButton, BButtonGroup, BFormSelect, BFormCheckbox, BBadge, BAlert, BOverlay, BSpinner } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import ECharts from 'vue-echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/grid';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import debounce from 'lodash/debounce';
import { onUnmounted } from '@vue/composition-api';
import store from '@/store';
import dashboardStoreModule from './dashboardStoreModule';

const PALETTE = ['#a51c30', '#5e5873', '#ff9f43', '#00a3c4', '#28c76f', '#b9b9c3'];
const TYPE_COLORS = { internal: '#a51c30', external: '#5e5873' };

// Local dates only: toISOString() would shift to UTC and can land on the wrong day.
function ymd(date) {
  return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
}

function parseYmd(value) {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

const PERIODS = [
  { value: 'this-month', text: 'This month' },
  { value: 'last-month', text: 'Last month' },
  { value: 'this-quarter', text: 'This quarter' },
  { value: 'last-3-months', text: 'Last 3 months' },
  { value: 'year-to-date', text: 'Year to date' },
  { value: 'last-12-months', text: 'Last 12 months' },
  { value: 'custom', text: 'Custom range…' },
];

// Start and end dates for a preset period.
function periodRange(period, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (period) {
    case 'last-month':
      return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    case 'this-quarter': {
      const q = Math.floor(m / 3) * 3;
      return [new Date(y, q, 1), new Date(y, q + 3, 0)];
    }
    case 'last-3-months':
      return [new Date(y, m - 2, 1), new Date(y, m + 1, 0)];
    case 'year-to-date':
      return [new Date(y, 0, 1), today];
    case 'last-12-months':
      return [new Date(y, m - 11, 1), new Date(y, m + 1, 0)];
    case 'this-month':
    default:
      return [new Date(y, m, 1), new Date(y, m + 1, 0)];
  }
}

const DEFAULT_FILTERS = { period: 'this-month', start: null, end: null, type: null, department: null, includeArchived: true };

// Filters live in the address bar, so a filtered report can be bookmarked or shared.
function filtersFromQuery(query) {
  const filters = { ...DEFAULT_FILTERS };
  if (PERIODS.some((p) => p.value === query.period)) filters.period = query.period;
  if (filters.period === 'custom' && /^\d{4}-\d{2}-\d{2}$/.test(query.start || '') && /^\d{4}-\d{2}-\d{2}$/.test(query.end || '')) {
    filters.start = query.start;
    filters.end = query.end;
  } else if (filters.period === 'custom') {
    filters.period = DEFAULT_FILTERS.period;
  }
  if (query.type === 'internal' || query.type === 'external') filters.type = query.type;
  if (query.department) filters.department = String(query.department);
  if (query.archived === '0') filters.includeArchived = false;
  return filters;
}

export default {
  components: { BCard, BButton, BButtonGroup, BFormSelect, BFormCheckbox, BBadge, BAlert, BOverlay, BSpinner, ECharts, vSelect, flatPickr },

  setup() {
    const MODULE = 'dashboard';
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, dashboardStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
    return {};
  },

  data() {
    const filters = filtersFromQuery(this.$route.query);
    return {
      filters,
      customRange: filters.start && filters.end ? `${filters.start} to ${filters.end}` : null,
      report: null,
      departments: [],
      loading: false,
      downloading: false,
      error: null,
      periodOptions: PERIODS,
      typeOptions: [
        { value: null, text: 'All' },
        { value: 'internal', text: 'Internal' },
        { value: 'external', text: 'External' },
      ],
      palette: PALETTE,
      rangeConfig: { mode: 'range', dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1, rangeSeparator: ' to ' } },
    };
  },

  computed: {
    dateRange() {
      if (this.filters.period === 'custom') {
        return this.filters.start && this.filters.end ? [this.filters.start, this.filters.end] : null;
      }
      return periodRange(this.filters.period).map(ymd);
    },

    periodLabel() {
      const preset = PERIODS.find((p) => p.value === this.filters.period);
      return preset ? preset.text : '';
    },

    isDefaultFilters() {
      return Object.keys(DEFAULT_FILTERS).every((key) => this.filters[key] === DEFAULT_FILTERS[key]);
    },

    approvalRate() {
      const { quotes, approvedQuotes } = this.report.summary;
      return quotes ? Math.round((approvedQuotes / quotes) * 100) : null;
    },

    quoteTurnaround() {
      const stage = this.report.stages.find((s) => s.key === 'quote');
      return stage ? stage.averageDays : null;
    },

    createdChart() {
      const buckets = this.report.createdOverTime.buckets;
      const series = ['internal', 'external'].map((type) => ({
        name: type === 'internal' ? 'Internal' : 'External',
        type: 'bar',
        stack: 'projects',
        barMaxWidth: 36,
        itemStyle: { color: TYPE_COLORS[type] },
        label: { show: true, position: 'inside', color: '#fff', formatter: (p) => (p.value ? p.value : '') },
        data: buckets.map((b) => b[type]),
      }));
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const bucket = buckets[params[0].dataIndex];
            const total = params.reduce((sum, p) => sum + p.value, 0);
            const range = bucket.start === bucket.end ? bucket.start : `${bucket.start} – ${bucket.end}`;
            return `<b>${range}</b><br>${params.map((p) => `${p.marker}${p.seriesName}: ${p.value}`).join('<br>')}<br>Total: ${total}`;
          },
        },
        legend: { top: 0, right: 0, data: ['Internal', 'External'] },
        grid: { left: 36, right: 12, top: 36, bottom: 28 },
        xAxis: { type: 'category', data: buckets.map((b) => b.label), axisTick: { show: false } },
        yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#ebe9f1' } } },
        series,
      };
    },

    costChart() {
      return {
        color: PALETTE,
        tooltip: { trigger: 'item', formatter: (p) => `${p.name}<br><b>${this.money(p.value)}</b> (${p.percent}%)` },
        series: [{
          type: 'pie',
          radius: ['55%', '85%'],
          avoidLabelOverlap: true,
          label: { show: false },
          data: this.report.costBreakdown.map((c) => ({ name: c.name, value: c.value })),
        }],
      };
    },

    materialChart() {
      return this.horizontalBar(this.report.materials, 'Parts');
    },

    rejectChart() {
      return this.horizontalBar(this.report.rejectReasons, 'Quotes');
    },
  },

  watch: {
    // Reload when any filter changes; brief debounce so a burst of changes makes one request.
    filters: {
      deep: true,
      handler() {
        this.syncQuery();
        this.debouncedLoad();
      },
    },

    customRange(value) {
      // flatpickr gives "start to end" once both ends are picked.
      const parts = String(value || '').split(' to ');
      if (parts.length === 2 && parts[0] && parts[1]) {
        this.filters.start = parts[0];
        this.filters.end = parts[1];
      }
    },

    'filters.period'(period) {
      if (period !== 'custom') {
        this.filters.start = null;
        this.filters.end = null;
        this.customRange = null;
      } else if (!this.filters.start && this.report) {
        // Start the custom range from what is on screen.
        this.customRange = `${this.report.range.start} to ${this.report.range.end}`;
      }
    },
  },

  created() {
    this.debouncedLoad = debounce(this.load, 250);
    this.requestId = 0;
    this.load();
  },

  mounted() {
    // Lets the print styles below hide the app's menu and top bar while this page is open.
    document.body.classList.add('report-print-page');
  },

  beforeDestroy() {
    document.body.classList.remove('report-print-page');
  },

  methods: {
    // The filters as the API expects them, or null while a custom range is still being picked.
    requestFilters() {
      const range = this.dateRange;
      if (!range) return null;
      return {
        startDate: range[0],
        endDate: range[1],
        type: this.filters.type || undefined,
        department: this.filters.department || undefined,
        includeArchived: this.filters.includeArchived ? 1 : 0,
      };
    },

    load() {
      const filters = this.requestFilters();
      if (!filters) return; // custom range still being picked

      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;

      store
        .dispatch('dashboard/getReportData', filters)
        .then((response) => {
          if (requestId !== this.requestId) return; // a newer request replaced this one
          this.report = response.data;
          this.departments = response.data.departments || [];
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;
          console.error(error);
          const data = error && error.response && error.response.data;
          this.error = (data && data.message && error.response.status < 500)
            ? data.message
            : 'The report could not be loaded. Please try again.';
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false;
        });
    },

    syncQuery() {
      const query = {};
      if (this.filters.period !== DEFAULT_FILTERS.period) query.period = this.filters.period;
      if (this.filters.period === 'custom' && this.filters.start && this.filters.end) {
        query.start = this.filters.start;
        query.end = this.filters.end;
      }
      if (this.filters.type) query.type = this.filters.type;
      if (this.filters.department) query.department = this.filters.department;
      if (!this.filters.includeArchived) query.archived = '0';
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.customRange = null;
    },

    print() {
      window.print();
    },

    // Printable PDF of the report with the current filters (api: getReportPdf, report-dashboard.blade.php).
    downloadPdf() {
      const filters = this.requestFilters();
      if (!filters) return;
      this.downloading = true;
      store
        .dispatch('dashboard/downloadReportPdf', filters)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `solstice-report-${filters.startDate}-to-${filters.endDate}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error(error);
          this.$toast({
            component: ToastificationContent,
            props: { title: 'PDF not created', text: 'The report PDF could not be created. Please try again.', icon: 'AlertTriangleIcon', variant: 'danger' },
          });
        })
        .finally(() => {
          this.downloading = false;
        });
    },

    money(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);
    },

    share(value, total) {
      return total ? `${Math.round((value / total) * 100)}%` : '0%';
    },

    stageShare(stage) {
      const longest = Math.max(...this.report.stages.map((s) => s.averageDays || 0));
      return stage.averageDays && longest ? `${Math.max(2, (stage.averageDays / longest) * 100)}%` : '0%';
    },

    barHeight(rows) {
      return `${Math.max(160, rows.length * 34 + 40)}px`;
    },

    horizontalBar(rows, unit) {
      const ordered = [...rows].reverse(); // largest at the top
      return {
        tooltip: { trigger: 'item', formatter: (p) => `${p.name}<br><b>${p.value}</b> ${unit.toLowerCase()}` },
        grid: { left: 8, right: 36, top: 8, bottom: 8, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#ebe9f1' } } },
        yAxis: {
          type: 'category',
          data: ordered.map((r) => r.name),
          axisTick: { show: false },
          axisLabel: { width: 150, overflow: 'truncate', formatter: (name) => (name.length > 26 ? `${name.slice(0, 25)}…` : name) },
        },
        series: [{
          type: 'bar',
          barMaxWidth: 22,
          itemStyle: { color: (p) => (p.name === 'Other' ? '#b9b9c3' : PALETTE[0]) },
          label: { show: true, position: 'right', color: '#5e5873' },
          data: ordered.map((r) => ({ name: r.name, value: r.value })),
        }],
      };
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

// Browser printing (Print button or Ctrl+P) on the Reports page: print only the report,
// without the app's side menu, top bar and page offsets. The body class is set while the page is open.
@media print {
  body.report-print-page .main-menu,
  body.report-print-page .header-navbar,
  body.report-print-page .header-navbar-shadow,
  body.report-print-page .footer,
  body.report-print-page .customizer,
  body.report-print-page .btn-scroll-to-top {
    display: none !important;
  }

  body.report-print-page .app-content {
    margin: 0 !important;
    padding: 0 !important;
  }

  body.report-print-page .content-wrapper,
  body.report-print-page .content-body {
    margin: 0 !important;
    padding: 0 !important;
  }

  body.report-print-page {
    background: #fff !important;
  }
}
</style>

<style lang="scss" scoped>
.report-page {
  padding-bottom: 2rem;
}

.report-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0.5rem 0 1rem;
}

.report-header-actions {
  display: flex;
  gap: 8px;
}

.filter-card {
  padding: 14px 16px 10px;
  margin-bottom: 1rem;
}

.filter-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 18px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  min-width: 150px;
}

.filter-field label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #5e5873;
}

.filter-field--range {
  min-width: 230px;
}

.filter-field--department {
  min-width: 240px;
  flex: 1 1 240px;
  max-width: 360px;
}

.filter-field--switch {
  padding-bottom: 6px;
}

.filter-field--reset {
  margin-left: auto;
}

.type-toggle .btn {
  min-width: 72px;
}

.filter-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #ebe9f1;
  font-size: 13px;
  color: #6e6b7b;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.filter-chip button {
  border: none;
  background: none;
  padding: 0 0 0 2px;
  color: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.kpi-card {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
}

.kpi-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #82868b;
}

.kpi-value {
  margin: 4px 0 2px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: #5e5873;
}

.kpi-value small {
  font-size: 14px;
  font-weight: 600;
}

.kpi-detail {
  font-size: 13px;
  color: #6e6b7b;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.chart-grid--wide-left {
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
}

@media (max-width: 991px) {
  .chart-grid,
  .chart-grid--wide-left {
    grid-template-columns: minmax(0, 1fr);
  }
}

.chart-card {
  margin-bottom: 1rem;
}

.chart-card-header {
  margin-bottom: 10px;
}

.chart-card-header h5 {
  margin-bottom: 0;
  font-weight: 600;
}

.chart {
  width: 100%;
  height: 260px;
}

.chart--tall {
  height: 300px;
}

.chart--donut {
  width: 200px;
  height: 200px;
  flex: 0 0 200px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 1px dashed #d8d6de;
  border-radius: 6px;
  color: #82868b;
  font-size: 13px;
  text-align: center;
  padding: 1rem;
}

.bar-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.bar-list li {
  margin-bottom: 10px;
}

.bar-list li.muted {
  opacity: 0.55;
}

.bar-list li.total {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #ebe9f1;
}

.bar-list-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 4px;
}

.stage-count {
  margin-left: 6px;
  font-size: 11px;
  color: #82868b;
}

.bar-track {
  height: 8px;
  border-radius: 999px;
  background: #f3f2f7;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  background: #a51c30;
  transition: width 0.3s ease;
}

.bar-fill--stage {
  background: #5e5873;
}

.breakdown {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.breakdown-table {
  flex: 1 1 220px;
  font-size: 13px;
}

.breakdown-table td {
  padding: 5px 6px;
  border-bottom: 1px solid #f3f2f7;
}

.swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 2px;
}

.loading-placeholder {
  min-height: 300px;
}

.report-error {
  margin-bottom: 1rem;
}

@media print {
  .filter-grid,
  .report-header-actions,
  .filter-chip button {
    display: none !important;
  }

  .filter-summary {
    border-top: none;
    margin-top: 0;
    padding-top: 0;
  }

  .chart-grid,
  .chart-grid--wide-left {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .chart-card,
  .kpi-card {
    break-inside: avoid;
  }
}
</style>
